<template>
  <div v-if="isTimerShown && finishByTime" class="time-touch-container no-svg">
    <!-- <svg viewBox="0 0 110 45" class="progress-ring">
      <rect
        class="progress-ring__track"
        x="2"
        y="2"
        width="106"
        height="41"
        rx="6"
        ry="6"
      />
      <rect
        ref="progressShape"
        class="progress-ring__progress"
        x="2"
        y="2"
        width="106"
        height="41"
        rx="6"
        ry="6"
        fill="transparent"
        stroke="#FFAE38"
        stroke-linecap="round"
        :style="{
          strokeDashoffset: strokeDashoffset,
        }"
      />
    </svg> -->
    <div class="initial-container">
      <div class="icon">⏰</div>
      <div class="initial-text">
        {{ showing ? eventPassedTime : eventRemainingTime }}
      </div>

      <div v-if="extraTimeForEvent.length && finishByTime" class="extra-time">
        +{{ Math.floor(extraTimeForEventSeconds / 60) }}
      </div>
    </div>
  </div>

  <div
    v-else-if="isTimerShown && !finishByTime"
    class="time-touch-container no-svg"
  >
    <div class="initial-container">
      <div class="icon">⏰</div>
      <div class="initial-text">{{ eventPassedTime }}</div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck
import { defineComponent } from 'vue';
import {
  CompetitionSubmitted,
  EventTimeFinished,
  HeroSuccessEvent,
  SubmitCompetitionClicked,
  UserStateLoadEvent
} from '@/app-events';
import EventBus from '@/plugin/vue-event-bus';
import remoteService from '@/services/remote-service';
import stateService from '@/services/state-service';
import { getUtcTime } from '@/utils/helpers';
import PageUtils, {
  EVENT_TIME,
  FINISH_BY_TIME,
  SHOW_EVENT_TIME
} from '@/utils/page-utils';

type EventTimer = {
  id: string;
  startTime: string;
  finishTime: string;
  fullTime: string | null;
  timeSpend: number | null;
};

type ExtraManagementEventTime = {
  id: string;
  value: number;
};

// Removed progressColor from ComponentDataType
type ComponentDataType = {
  showing: boolean;
  percentage: number;
  timers: Array<number>;
  eventPassedTime: null | string;
  eventRemainingTime: null | string;
  baseTime: number;
  baseTimeSeconds: number;
  totalTimeSpend: number;
  extraTimeForEvent: Array<ExtraManagementEventTime>;
  extraTimeForEventSeconds: number;
  circumference: number;
  strokeDashoffset: number | string;
};

let callback: any = null;
const refetchtimes = [5, 10, 30, 60, 120, 180, 300];

export default defineComponent({
  mounted() {
    EventBus.listen(UserStateLoadEvent, this.startTimers);
    EventBus.listen(HeroSuccessEvent, this.stopTimers);
    EventBus.listen(SubmitCompetitionClicked, this.submitCompetitionData);
  },
  destroyed() {
    EventBus.remove(UserStateLoadEvent, this.startTimers);
    EventBus.remove(HeroSuccessEvent, this.stopTimers);
    EventBus.remove(SubmitCompetitionClicked, this.submitCompetitionData);
  },
  beforeDestroy() {
    this.timers.forEach(clearInterval);
  },
  data(): ComponentDataType {
    return {
      showing: false,
      timers: [],
      eventPassedTime: null,
      eventRemainingTime: null,

      percentage: 0,

      baseTime: 0,
      baseTimeSeconds: 0,
      totalTimeSpend: 0,
      extraTimeForEvent: [],
      extraTimeForEventSeconds: 0,

      circumference: 0,
      // Initial offset set to 0. This will be updated to 'c' in setupSvg().
      strokeDashoffset: 0
    };
  },
  computed: {
    totalStars(): number {
      return this.$store.getters['totalStars'] || 0;
    },
    totalSolutionCount(): number {
      return this.$store.getters['totalSolutionCount'] || 0;
    },
    competition() {
      return this.$store.getters['competition/competition'] as {
        isLive: boolean;
        showTimer: boolean;
        finishByTimer: boolean;
        duration: number;
      };
    },
    isTimerShown(): boolean {
      const showEventTime = this.competition.isLive
        ? this.competition.showTimer
        : SHOW_EVENT_TIME;

      return PageUtils.isStudioEvent() && showEventTime;
    },
    finishByTime() {
      return this.competition.isLive
        ? this.competition.finishByTimer
        : FINISH_BY_TIME;
    }
  },
  methods: {
    setupSvg() {
      const progressShape = this.$refs.progressShape as SVGGeometryElement;

      if (progressShape) {
        const c = progressShape.getTotalLength();
        this.circumference = c;

        progressShape.style.strokeDasharray = `${c} ${c}`;

        // Set initial offset to C to hide the bar
        this.strokeDashoffset = c;
      }
    },
    submitCompetitionData(event: { callback?: () => null } = {}) {
      callback = event.callback;
      let eventTimers = this.getEventTimers();
      let currentEventTimer = eventTimers[eventTimers.length - 1];
      eventTimers = this.getEventTimers().map((timer) => {
        if (timer.id === currentEventTimer.id) {
          return this.closeEventTimer(currentEventTimer);
        }
        return timer;
      });
      this.timers.forEach(clearInterval);
      const userState = stateService.getUserState();
      userState.params.eventTimers = eventTimers;
      stateService.saveUserState(userState);
      this.patchAndSaveUserState();
    },
    stopTimers() {
      if (
        PageUtils.isEndOfStudioEvent(
          this.competition,
          this.totalSolutionCount
        ) ||
        PageUtils.isStudioEventChallengesCompletedWith3Stars(
          this.competition,
          this.totalStars
        )
      ) {
        this.timers.forEach(clearInterval);
      }
    },
    setup(cb = () => null) {
      const userState = stateService.getUserState();
      let p = userState.managementParams;

      this.baseTime = this.competition.isLive
        ? +this.competition.duration
        : EVENT_TIME;
      this.baseTimeSeconds = this.baseTime * 60;
      this.extraTimeForEvent = [];
      this.extraTimeForEventSeconds = 0;
      if (Array.isArray(p.extraTimeForEvent)) {
        this.extraTimeForEvent =
          p.extraTimeForEvent as Array<ExtraManagementEventTime>;
        this.extraTimeForEventSeconds = this.extraTimeForEvent.reduce(
          (acc: number, t: ExtraManagementEventTime) => {
            return acc + t.value;
          },
          0
        );
      }

      if (typeof cb === 'function') {
        cb();
      }
    },
    isEventTimerObject(object: unknown): object is EventTimer {
      if (
        typeof object !== 'object' ||
        object === null ||
        object === undefined
      ) {
        return false;
      }
      const timer = object as Record<string, unknown>;

      if (!this.competition.finishByTimer) {
        return 'startTime' in timer && typeof timer.startTime === 'string';
      }

      return (
        'startTime' in timer &&
        typeof timer.startTime === 'string' &&
        'finishTime' in timer &&
        typeof timer.finishTime === 'string' &&
        'fullTime' in timer &&
        (typeof timer.fullTime === 'string' || timer.fullTime === null) &&
        'timeSpend' in timer &&
        (typeof timer.timeSpend === 'number' || timer.timeSpend === null)
      );
    },
    isTimerActive(eventTimer: EventTimer) {
      if (!this.isEventTimerObject(eventTimer)) {
        return false;
      }

      if (!this.competition.finishByTimer) {
        return true;
      }

      if (
        typeof eventTimer.fullTime === 'string' &&
        typeof eventTimer.timeSpend === 'number'
      ) {
        return false;
      }

      return getUtcTime(eventTimer.finishTime).isAfter(getUtcTime());
    },
    createEventTimerObject(
      totalTime: number,
      id: string = crypto.randomUUID()
    ) {
      const startTime = getUtcTime();

      const timer: EventTimer = {
        id,
        startTime: startTime.format(),
        finishTime: startTime.add(totalTime, 'seconds').format(),
        fullTime: null,
        timeSpend: null
      };

      this.createIntervalTimer(timer);
      let eventTimers = this.getEventTimers();
      eventTimers.push(timer);
      const userState = stateService.getUserState();
      userState.params.eventTimers = eventTimers;
      userState.submitted = false;
      stateService.saveUserState(userState);
      this.patchAndSaveUserState();
    },
    closeEventTimer(timer: EventTimer): EventTimer {
      const updated = { ...timer };
      updated.fullTime = updated.finishTime;
      updated.finishTime = getUtcTime().format();
      if (
        getUtcTime(updated.finishTime).isAfter(getUtcTime(updated.fullTime))
      ) {
        updated.finishTime = updated.fullTime;
      }
      updated.timeSpend = getUtcTime(updated.finishTime).diff(
        getUtcTime(updated.startTime)
      );
      return updated;
    },
    createIntervalTimer(currentEventTimer: EventTimer) {
      this.timers.push(
        setInterval(() => {
          const eventFinishTime = getUtcTime(currentEventTimer.finishTime);
          const eventStartTime = getUtcTime(currentEventTimer.startTime);
          const currentTime = getUtcTime();
          const passedTimeFromCurrentTimer = currentTime.diff(
            eventStartTime,
            'seconds'
          );
          const remainingTimeInSeconds =
            eventFinishTime.diff(currentTime, 'seconds') + 1;

          this.setEventRemainingTime(remainingTimeInSeconds);

          if (refetchtimes.includes(remainingTimeInSeconds)) {
            remoteService.loadUserState(false).then(() => this.setup());
          }

          if (this.finishByTime && remainingTimeInSeconds <= 0) {
            this.timers.forEach(clearInterval);

            const eventTimers = this.getEventTimers().map((timer) => {
              if (timer.id === currentEventTimer.id) {
                return this.closeEventTimer(currentEventTimer);
              }
              return timer;
            });
            const userState = stateService.getUserState();
            userState.params.eventTimers = eventTimers;
            stateService.saveUserState(userState);

            if (this.extraTimeForEvent.length) {
              return this.checkNextPossibleTimers();
            }

            userState.params.showSubmitFlow = false;
            userState.submitted = true;
            stateService.saveUserState(userState);
            this.patchAndSaveUserState();

            const currentTimerDuration = getUtcTime(
              currentEventTimer.finishTime
            ).diff(eventStartTime, 'seconds');
            const clampedPassedFromCurrentTimer = Math.min(
              Math.max(passedTimeFromCurrentTimer, 0),
              currentTimerDuration
            );
            this.setEventRemainingTime(0);
            this.setEventPassedTime(
              this.totalTimeSpend + clampedPassedFromCurrentTimer
            );

            this.$store.commit('setIsStudioEventTimeFinished');
            EventBus.publish(new EventTimeFinished());
            return null;
          }

          this.setEventPassedTime(
            this.totalTimeSpend + passedTimeFromCurrentTimer
          );
        }, 1000)
      );
    },
    async patchAndSaveUserState() {
      try {
        await remoteService.updateUserState();
      } finally {
        if (typeof callback === 'function') {
          callback();
          callback = null;
        }
      }
    },
    getEventTimers() {
      const userState = stateService.getUserState();
      if (!userState.params.eventTimers) {
        userState.params.eventTimers = [];
      }
      const eventTimers = userState.params.eventTimers as Array<EventTimer>;
      this.totalTimeSpend = eventTimers.reduce((acc, t) => {
        const ts = (typeof t.timeSpend === 'number' ? t.timeSpend : 0) / 1000;
        return acc + ts;
      }, 0);

      return eventTimers;
    },
    startTimers() {
      if (!this.isTimerShown) {
        return;
      }

      this.setupSvg();
      this.setup();
      const userState = stateService.getUserState();
      const eventTimers = this.getEventTimers();
      const timerIds = eventTimers.map((t) => t.id);
      const hasExtraTimerThatIsNotClosed = this.extraTimeForEvent.find(
        (extraTime) => {
          return !timerIds.includes(extraTime.id);
        }
      );
      if (
        userState.submitted &&
        this.competition.finishByTimer &&
        !hasExtraTimerThatIsNotClosed
      ) {
        this.setEventPassedTime(this.totalTimeSpend);
        this.setEventRemainingTime(0);
        this.$store.commit('setIsStudioEventTimeFinished');
        EventBus.publish(new CompetitionSubmitted());
        return;
      }

      let initialTimer = eventTimers[0];
      const hasInitialTimer = this.isEventTimerObject(initialTimer);
      if (!hasInitialTimer) {
        this.createEventTimerObject(this.baseTimeSeconds);
      } else if (this.isTimerActive(initialTimer)) {
        this.createIntervalTimer(initialTimer);
      } else if (!this.isTimerActive(initialTimer) && !initialTimer.timeSpend) {
        const eventTimers = this.getEventTimers().map((timer) => {
          if (timer.id === initialTimer.id) {
            return this.closeEventTimer(initialTimer);
          }
          return timer;
        });

        const userState = stateService.getUserState();
        userState.params.eventTimers = eventTimers;
        stateService.saveUserState(userState);

        this.checkNextPossibleTimers();
      } else {
        this.checkNextPossibleTimers();
      }
    },
    checkNextPossibleTimers() {
      const userState = stateService.getUserState();
      const eventTimers = this.getEventTimers();
      const timerIds = eventTimers.map((t) => t.id);

      const nextTimer = eventTimers[eventTimers.length - 1];
      if (this.isTimerActive(nextTimer)) {
        this.createIntervalTimer(nextTimer);
      } else {
        let timersToSave = eventTimers;
        const shouldCloseExpiredTimer = !nextTimer.timeSpend;
        if (shouldCloseExpiredTimer) {
          timersToSave = eventTimers.map((timer) => {
            if (timer.id === nextTimer.id) {
              return this.closeEventTimer(nextTimer);
            }
            return timer;
          });
          userState.params.eventTimers = timersToSave;
          stateService.saveUserState(userState);
        }

        const found = this.extraTimeForEvent.find((extraTime) => {
          return !timerIds.includes(extraTime.id);
        });

        if (found) {
          this.createEventTimerObject(found.value, found.id);
        } else {
          const shouldSubmit = !userState.submitted;
          const shouldPatch = shouldSubmit || shouldCloseExpiredTimer;
          if (shouldSubmit) {
            userState.submitted = true;
            stateService.saveUserState(userState);
          }
          if (shouldPatch) {
            this.patchAndSaveUserState();
          }
          this.setEventPassedTime(this.totalTimeSpend);
          this.setEventRemainingTime(0);
          this.$store.commit('setIsStudioEventTimeFinished');
          setTimeout(() => {
            EventBus.publish(new CompetitionSubmitted());
          }, 1500);
        }
      }
    },
    setEventPassedTime(totalSeconds: number) {
      const mins = Math.floor(totalSeconds / 60).toString();
      const seconds = (totalSeconds - Number(mins) * 60).toString();
      this.eventPassedTime = `${mins.padStart(2, '0')}:${seconds.padStart(
        2,
        '0'
      )}`;
    },
    setEventRemainingTime(totalSeconds: number) {
      const mins = Math.floor(totalSeconds / 60).toString();
      const seconds = (totalSeconds - Number(mins) * 60).toString();
      this.eventRemainingTime = `${mins.padStart(2, '0')}:${seconds.padStart(
        2,
        '0'
      )}`;

      const totalDuration =
        this.baseTimeSeconds + this.extraTimeForEventSeconds;
      const timeRemainingRatio = Math.max(0, totalSeconds / totalDuration);
      const timeSpentRatio = 1 - timeRemainingRatio;
      this.strokeDashoffset = this.circumference * (1 - timeSpentRatio);
    }
  }
});
</script>

<style lang="scss" scoped>
.time-touch-container {
  z-index: 1000;
  background-color: transparent;
  position: absolute;
  left: 40%;
  font-weight: bold;
  font-size: 1.25rem;
  line-height: 1.25rem;
  font-weight: bold;
  color: white;
  border-radius: 4px;
  user-select: none;
  height: 45px;
  min-width: 110px;
  display: grid;
  place-items: center;

  --ring-thickness: 4px;
  --progress-color: #ffae38;

  transform: scale(0);
  animation: scale-in 350ms 2s forwards ease-out;

  .initial-container {
    z-index: 2;
    width: 100%;
    height: 100%;

    display: grid;
    place-items: center;
    grid-template-columns: auto 1fr;
    background-color: black;
    border-radius: 0.5rem;

    .icon {
      padding-left: 10px;
    }

    //.initial-text {
    //  cursor: pointer;
    //}

    .extra-time {
      display: grid;
      place-items: center;
      font-size: 1.2rem;
      color: white;
      border-radius: 0.5rem;
      background-color: rgb(84, 20, 42);
      width: 2.5rem;
      height: 2.5rem;
      transform: scale(0);
      place-self: center;
      animation: scale-in 300ms ease-in-out forwards;
      animation-delay: 1000ms;

      position: absolute;
      left: 115px;
    }
  }

  &.no-svg {
    height: 39px;
    border: var(--ring-thickness) solid #555;

    .initial-container {
      background-color: black;
      border-radius: 0;

      /*
      .initial-text {
        cursor: auto;
      }
      */
    }
  }
}

.progress-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
}

.progress-ring__track,
.progress-ring__progress {
  stroke-width: var(--ring-thickness);
  fill: transparent;
}

.progress-ring__track {
  stroke: #555;
  opacity: 0.5;
}

.progress-ring__progress {
  transition: stroke-dashoffset 1s linear;
  stroke-linecap: round;
}

@keyframes scale-in {
  0% {
    transform: scale(0);
  }

  90% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
}
</style>
